import type { ContentType, SortByOption } from '@kimuno/api-sdk';
import * as yup from 'yup';

import { pageContentTypes, searchSettings } from '../../constants';
import type { PageContentType } from '../../types/search';

// query-string parses a valueless key (`?key`) to null and `?key=` to ''. yup 1's
// array and number casts throw on both, and the SEAR-518 fallback in
// queryToSearchFilters then dropped every filter in the URL, so treat both as absent.
const isValueless = (value: unknown) => value === null || value === '';

// Note, yup has an `ensure` function that uses this behavior, however
// it also has the non optional side effect of converting undefined to
// an empty array, which then creates problems
const stringArray = <T extends string = string>(values?: T[]) =>
	yup
		.array()
		.of(values ? yup.string().oneOf(values) : yup.string())
		.transform((value, originalValue) => {
			if (isValueless(originalValue)) {
				return undefined;
			}
			if (originalValue && !Array.isArray(originalValue)) {
				return [originalValue];
			}
			return value;
		}) as unknown as yup.ArraySchema<T[] | undefined, yup.AnyObject>;

const numberValue = () =>
	yup.number().transform((value, originalValue) => (isValueless(originalValue) ? undefined : value));

const stringValue = <T extends string = string>(values?: T[], defaultValue?: T) => {
	// query-string's `arrayFormat: 'comma'` splits a value that contains commas
	// into an array. yup 0.x silently re-joined it via `toString()`; yup 1 leaves
	// arrays untouched and the cast throws, which drops every filter in the URL.
	const stringSchema = (values ? yup.string().oneOf(values) : yup.string()).transform(
		(value, originalValue) => (Array.isArray(originalValue) ? originalValue.join(',') : value)
	);
	const stringSchemaWithDefault = defaultValue ? stringSchema.default(defaultValue) : stringSchema;
	return yup.lazy((value) =>
		value === null ? yup.mixed().strip(true) : stringSchemaWithDefault
	) as yup.Lazy<T>;
};

const searchQuerySchema = yup
	.object({
		contentType: stringArray<ContentType>(['course', 'document', 'video']),
		pageContentType: stringArray<PageContentType>(pageContentTypes.map((t) => t.key)),
		learningFormat: stringArray(),
		classYear: stringArray(),
		maxClassYear: numberValue(),
		minClassYear: numberValue(),
		publisher: stringArray(),
		schoolType: stringArray(),
		subject: stringArray(),
		subTopic: stringArray(),
		sortBy: stringValue<SortByOption>(['relevance', 'createdDate'], searchSettings.defaultSortBy),
		teachingFormat: stringArray(),
		teachingMethod: stringArray(),
		// Coerce only the genuine boolean-ish values; anything else (e.g. a malformed
		// double-encoded query blob, SEAR-518) degrades to undefined instead of throwing.
		// yup.boolean()'s built-in coercion asserts the type and would throw on a bad
		// string, so we transform off yup.mixed() and emit boolean | undefined ourselves.
		inclusionAdapted: yup.mixed().transform((_value, originalValue) => {
			if (originalValue === true || originalValue === 'true') return true;
			if (originalValue === false || originalValue === 'false') return false;
			return undefined;
		}) as yup.Schema<boolean | undefined>,
		term: stringValue(),
		workload: stringArray(),
		courseTheme: stringArray(),
		courseType: stringArray(),
		courseDuration: stringArray(),
	})
	.noUnknown(true);

export default searchQuerySchema;
